import { round } from 'mathjs';
import { evaluateFormulaRaw, formulaParses } from './formula-scope.js';
/**
 * Mulberry32 PRNG — fast, deterministic, portable 32-bit generator.
 * Returns a function that yields floats in [0, 1) from the given seed.
 */
function mulberry32(seed) {
    let s = seed >>> 0;
    return function () {
        s += 0x6d2b79f5;
        let z = s;
        z = Math.imul(z ^ (z >>> 15), z | 1);
        z ^= z + Math.imul(z ^ (z >>> 7), z | 61);
        return ((z ^ (z >>> 14)) >>> 0) / 0x100000000;
    };
}
/**
 * Derives a per-variable sub-seed from the attempt seed and variable id.
 * Uses FNV-1a over the id string so variable order doesn't affect values.
 */
function deriveSeed(attemptSeed, variableId) {
    let hash = 0x811c9dc5;
    for (let i = 0; i < variableId.length; i++) {
        hash ^= variableId.charCodeAt(i);
        hash = (Math.imul(hash, 0x01000193) >>> 0);
    }
    return (attemptSeed ^ hash) >>> 0;
}
/**
 * Formats a rolled value using the same scientific/standard notation thresholds
 * used throughout the app (|n| < 1e-5 or >= 1e5 → scientific, up to 6 sig figs).
 */
function fmtValue(n) {
    const abs = Math.abs(n);
    const notation = n !== 0 && (abs < 1e-5 || abs >= 1e5) ? 'scientific' : 'standard';
    return new Intl.NumberFormat('en-US', { notation, maximumSignificantDigits: 6 }).format(n);
}
/**
 * What a value carries when nothing has asked it to round — and therefore what it IS, since
 * rounding happens during resolution and the rounded number is what propagates.
 *
 * FIFTEEN, which is "the full number": the exact range of a double. It is not an arbitrary cap.
 * Below it a value would be silently shortened — 310/3 came back as 103.33333 when this was six,
 * which is a teacher's number quietly truncated without anyone asking. Above it, float noise
 * starts showing: the 17th digit of 0.1*3 is where 0.30000000000000004 lives, and fifteen is
 * precisely the width that cleans that while keeping everything real.
 *
 * So blank in the Decimals field means the full number, not a default rounding. A teacher who
 * wants it shorter says so, and that declaration then replaces this entirely.
 */
const FULL_PRECISION_DIGITS = 15;
/** The one rounding every resolved value passes through, whatever produced it. Applied to the
 * NUMBER, never to a formatted string — formatting is locale-dependent and must never be the thing
 * that decides a value. */
function toResolvedPrecision(n, figs = FULL_PRECISION_DIGITS) {
    return Number(n.toPrecision(figs));
}
/** The range a declared `decimals` may take — `round`'s own useful range. Zero is meaningful
 *  (round to a whole number); past ten it is below the precision a double can carry anyway. */
export const MIN_EQUATION_DECIMALS = 0;
export const MAX_EQUATION_DECIMALS = 10;
/** `toPrecision`'s useful range here. One figure is a legitimate ask; past fifteen is past what a
 *  double carries, which is what `FULL_PRECISION_DIGITS` already says. */
export const MIN_EQUATION_SIG_FIGS = 1;
export const MAX_EQUATION_SIG_FIGS = 15;
/**
 * What a stored `skew` may be.
 *
 * The floor is 51 because 50 IS the uniform draw, which is spelled as no skew at all — a stored
 * 50 would be a second spelling of the default.
 *
 * The ceiling is 95 because of what a skew does to the single likeliest value, not because of
 * how it looks: on a 2493-point range, 95 already puts 56% of all draws on the minimum alone,
 * and 98 puts 80% there. There is no authoring use past that, only a variable that has stopped
 * varying. The cap cannot prevent that on its own, though — a three-point range is past half on
 * one value at 65 — so `skewModeShare` is what actually catches it, per range.
 */
export const MIN_SKEW = 51;
export const MAX_SKEW = 95;
/** Past this share on a single value, a random has effectively stopped being random. Half is the
 *  line because it is the one that can be stated: one value comes up more often than every other
 *  value put together. */
export const DEGENERATE_SKEW_SHARE = 0.5;
/** Every variable name an equation references, in `{name}` form. Duplicates collapse: this answers
 * "what must exist before this can be computed", not "how often is each used". */
export function formulaDependencies(formula) {
    const names = new Set();
    for (const m of formula.matchAll(/\{([^}]+)\}/g)) {
        const name = (m[1] ?? '').trim();
        if (name)
            names.add(name);
    }
    return [...names];
}
/**
 * What joins a set's name to a member's leaf, and it is not a preference.
 *
 * `stripVariableChips` turns `{name}` into the BARE name and `buildScopeWithValues` keys the
 * scope by it, so a composed name becomes a mathjs IDENTIFIER. Measured against the real
 * evaluator with `StarA = 99` and `mass = 7` also in scope:
 *
 *   StarA_mass  -> 2    the variable
 *   StarA.mass  -> throws, "No access to property mass"
 *   StarA-mass  -> 92   silently computes StarA - mass
 *   StarA|mass  -> 103  silently computes a bitwise or
 *
 * The last two do not error. They return a plausible wrong number, which is the worst failure
 * this codebase keeps meeting, so underscore is the only safe choice.
 */
export const SET_CONNECTOR = '_';
/**
 * A member's name is the COMPOSED one, and that is the whole trick.
 *
 * `set` is a display grouping; `name` stays what it has always been — the full, unique identifier
 * every chip, formula, roll map and rename already keys on. Nothing downstream learns about sets,
 * which is why this is not v1's `familyId` returning: that sat BESIDE the name as a second claim
 * about identity and could disagree with it. This one cannot, because the invariant below ties
 * it to the name, and `variableSetInvariant` is what proves it.
 */
export function composeSetName(set, leaf) {
    return `${set}${SET_CONNECTOR}${leaf}`;
}
/** What the panel shows on a member's row: the leaf alone, since the set names itself above. */
export function setLeaf(v) {
    return v.set && v.name.startsWith(v.set + SET_CONNECTOR)
        ? v.name.slice(v.set.length + SET_CONNECTOR.length)
        : v.name;
}
/**
 * Is this variable's `set` consistent with its own name?
 *
 * The one thing that could make a set a second source of truth, checked rather than assumed.
 * A member whose name has drifted from its set is not a display problem: the panel would file it
 * under a heading it no longer belongs to, and a set rename would miss it.
 */
export function variableSetInvariant(v) {
    return !v.set || v.name.startsWith(v.set + SET_CONNECTOR);
}
/** The sets present among these variables, in first-seen order. An EMPTY set is not here and
 *  cannot be: it has no member to carry the name, which is exactly why one is never stored. */
export function setsIn(variables) {
    const seen = [];
    for (const v of variables) {
        if (v.set && !seen.includes(v.set))
            seen.push(v.set);
    }
    return seen;
}
/**
 * WHICH OF THE THREE a row is, read off which fields EXIST rather than a stored flag.
 *
 * Presence, not validity: a constant whose value the teacher has not typed yet is still a
 * constant, and an equation with an empty formula is still an equation. Keying on validity
 * instead would make a half-filled row change type under the teacher as they fill it in, and the
 * editor would redraw a different set of fields mid-edit. "Is it complete?" is a separate
 * question, asked by `isSavableVariable`.
 *
 * No mode field exists on purpose — three distinct field signatures cannot disagree with
 * themselves the way a flag sitting beside them can. `formula` wins over `value` so that
 * switching a row's type is unambiguous even if a stale field survives, and random is the
 * default because every variable authored before the three types existed is one.
 */
export function variableKind(v) {
    if (v.formula !== undefined && v.formula !== null)
        return 'equation';
    if (v.value !== undefined && v.value !== null)
        return 'constant';
    return 'random';
}
/** The fields that do NOT belong to a kind, cleared so a switched row cannot carry a stale one.
 *  Returned as an explicit patch of `undefined`s rather than by omission — see
 *  `feedback_structural_patch_deletes_by_omission`: omitting is how a field gets deleted by
 *  accident, so deleting on purpose has to be said out loud. */
export function fieldsForKind(kind) {
    // The SETTINGS go with their kind, not just the definition fields: a random switched to an
    // equation and back used to arrive still carrying the skew it was authored with, which then
    // applied to a range the teacher had retyped from scratch.
    const randomOnly = { skew: undefined, skewToward: undefined };
    const equationOnly = { decimals: undefined, sigFigs: undefined };
    if (kind === 'constant')
        return { value: NaN, min: undefined, max: undefined, step: undefined, formula: undefined, ...randomOnly, ...equationOnly };
    if (kind === 'equation')
        return { formula: '', value: undefined, min: undefined, max: undefined, step: undefined, ...randomOnly };
    return { min: NaN, max: NaN, step: NaN, value: undefined, formula: undefined, ...equationOnly };
}
/** Switching an equation between the two rounding kinds. Explicit `undefined` for the one being
 *  left, never omission: omitting is how a field survives a switch and then outranks the one the
 *  teacher just set, since `decimals` is read first. */
export function fieldsForRounding(kind, n) {
    return kind === 'decimals'
        ? { decimals: n, sigFigs: undefined }
        : { sigFigs: n, decimals: undefined };
}
/**
 * The order equations have to be computed in, worked out once.
 *
 * `ordered` lists every equation that CAN be computed, each after everything it depends on.
 * `unresolvable` names everything that cannot be: an equation referencing a name nothing defines,
 * one caught in a cycle, anything downstream of either, and any non-equation row too incomplete
 * to have a value. Partial, not all-or-nothing — one broken equation must not take the rest of
 * the task's variables down with it.
 */
export function planResolution(variables) {
    const available = new Set();
    const equations = [];
    const incomplete = [];
    for (const v of variables) {
        if (!v.name)
            continue;
        const kind = variableKind(v);
        if (kind === 'equation') {
            // An equation with nothing written in it yet is a draft, not a dependency anything can
            // stand on — treat it as unresolvable rather than as an equation over no variables.
            if (v.formula.trim())
                equations.push(v);
            else
                incomplete.push(v.name);
            continue;
        }
        const complete = kind === 'constant' ? !isNaN(v.value) : isUsableRange(v);
        if (complete)
            available.add(v.name);
        else
            incomplete.push(v.name);
    }
    const ordered = [];
    let pending = equations;
    for (;;) {
        const deferred = [];
        for (const v of pending) {
            if (formulaDependencies(v.formula).every(name => available.has(name))) {
                ordered.push(v);
                available.add(v.name);
            }
            else
                deferred.push(v);
        }
        // A pass that placed nothing means everything left is blocked for good: a cycle, or a
        // dependency on something already unresolvable. Without this the loop never ends.
        if (deferred.length === pending.length)
            return { ordered, unresolvable: [...incomplete, ...deferred.map(v => v.name)] };
        pending = deferred;
    }
}
/**
 * Every variable that would be computed FROM this one, however many steps away.
 *
 * The direction matters and is easy to get backwards: this is not what `varName` depends on, it is
 * what depends on `varName`. `mass` → `{luminosity, magnitude}` when `luminosity = {mass}^3.5` and
 * `magnitude` is computed from `luminosity`.
 */
export function dependentsOf(varName, variables) {
    const found = new Set();
    if (!varName)
        return found;
    let grew = true;
    while (grew) {
        grew = false;
        for (const v of variables) {
            if (!v.name || found.has(v.name) || v.name === varName || !v.formula)
                continue;
            const deps = formulaDependencies(v.formula);
            if (deps.includes(varName) || deps.some(d => found.has(d))) {
                found.add(v.name);
                grew = true;
            }
        }
    }
    return found;
}
/**
 * The names this variable's own equation must NOT reference, because each would close a loop.
 *
 * Itself, and everything already computed from it. This is the PREVENTION half of cycle safety:
 * handed to the formula field as its variable list, it means the chip picker cannot offer a
 * circular reference and a typed `{name}` for one never becomes a chip. Detection still has to
 * exist alongside it — a teacher can type braces, and a rename or a paste can create a loop
 * between two formulas neither of which was edited — which is what `planResolution`'s
 * `unresolvable` reports.
 *
 * A name that is merely unresolvable for some other reason is NOT in here: being broken is not the
 * same as being circular, and refusing to let a teacher reference it would be a second,
 * confusing verdict about an unrelated problem.
 */
export function wouldCycle(varName, variables) {
    const forbidden = dependentsOf(varName, variables);
    if (varName)
        forbidden.add(varName);
    return forbidden;
}
export function equationProblem(variable, variables) {
    if (!variable.formula?.trim())
        return null;
    /*
     * SYNTAX FIRST, because the other two questions assume the formula means something.
     *
     * `{star1_lum}/(4*pi*{star1_radius}^2))^0.25` — one closing parenthesis too many — named two
     * real variables and formed no cycle, so it passed both checks below and failed silently at
     * evaluation, where the resolver cannot tell a broken expression from a dependency that has
     * not been computed yet and omits the variable either way. The teacher got no value and no
     * error.
     */
    if (!formulaParses(variable.formula))
        return { kind: 'syntax' };
    const deps = formulaDependencies(variable.formula);
    const defined = new Set(variables.filter(v => v.name).map(v => v.name));
    const unknown = deps.filter(name => !defined.has(name));
    if (unknown.length > 0)
        return { kind: 'unknown', names: unknown };
    // A dependency that is itself computed FROM this variable is what closes the loop, so naming
    // those references says where the problem is rather than only that there is one.
    const forbidden = wouldCycle(variable.name, variables);
    const circular = deps.filter(name => forbidden.has(name));
    return circular.length > 0 ? { kind: 'cycle', names: circular } : null;
}
/**
 * Resolves every variable to the value a student will see, in DEPENDENCY order.
 *
 * The three kinds resolve differently and only one of them is random, so the caller supplies that
 * one step: `sampleRandom` is the ONLY thing that differs between the teacher's preview (an
 * unseeded reroll) and the student's attempt (a seeded, reproducible draw). Everything else —
 * which kind a variable is, what order equations compute in, what rounding applies, what happens
 * to a cycle — is decided here, once, so preview and runtime cannot drift. They did drift before:
 * this file and the app's `rollVariableValues` were independent implementations of the same idea.
 *
 * Order is topological, never the order the teacher happens to have dragged the rows into.
 * Resolution repeats over the unresolved equations until a pass computes nothing new, so an
 * equation may reference one defined later in the list.
 *
 * A variable is OMITTED from the result — rather than guessed at — when it cannot be resolved:
 * an incomplete definition, an equation referencing a name that does not exist, an equation that
 * evaluates to NaN or Infinity, or a dependency cycle. Omission is contagious by construction,
 * since a dependent's names then never all appear. That is the fail-closed half of the contract:
 * a chip with no value renders as a chip, which is visibly wrong, where a chip holding a silently
 * invented number is not.
 */
export function resolveVariables(variables, opts) {
    const values = {};
    for (const v of variables) {
        if (!v.name)
            continue;
        const kind = variableKind(v);
        if (kind === 'equation')
            continue;
        if (kind === 'constant') {
            if (!isNaN(v.value))
                values[v.name] = toResolvedPrecision(v.value);
            continue;
        }
        if (!isUsableRange(v))
            continue;
        const drawn = opts.sampleRandom(v);
        if (isFinite(drawn))
            values[v.name] = toResolvedPrecision(drawn);
    }
    // Equations consume their dependencies as plain `String(n)`, NOT as the formatted display text.
    // The two agree numerically because the rounding above already happened; going through display
    // text instead would push every chain through the viewer's locale, where `1,5` parses as 1.
    for (const v of planResolution(variables).ordered) {
        const scope = {};
        let ready = true;
        for (const name of formulaDependencies(v.formula)) {
            // The PLAN says the dependency is defined; whether it produced a number is another matter —
            // a random whose draw came back NaN is planned for and still has no value.
            if (!(name in values)) {
                ready = false;
                break;
            }
            scope[name] = String(values[name]);
        }
        if (!ready)
            continue;
        // The EQUATION'S OWN mode, not the caller's: that is the whole point of storing it per
        // variable. The caller's is the fallback for one stored before the mode existed.
        const n = evaluateFormulaRaw(v.formula, scope, v.angleMode ?? opts.angleMode ?? 'degrees');
        // The declared rounding, where there is one, IS the value — so it is what the next equation
        // in the chain reads, not a prettier version of something else. One function either way:
        // the default is six significant figures, and a declared setting only narrows the same thing.
        if (!isFinite(n))
            continue;
        /*
         * A DECLARED rounding is the teacher's statement about the value, so it is the last word:
         * the full-precision cleanup does NOT also apply on top of it.
         *
         * `round` is mathjs's own, not a reimplementation, so typing `round({x}/3, 2)` into the
         * formula and typing 2 into the field cannot drift apart.
         */
        values[v.name] = v.decimals != null ? round(n, v.decimals)
            // Sig figs go through the SAME function as the no-rounding default, with a narrower width:
            // the default already is `toPrecision(15)`, so a declared 3 is the same operation, not a
            // second kind of rounding bolted on beside it.
            : v.sigFigs != null ? toResolvedPrecision(n, v.sigFigs)
                : toResolvedPrecision(n);
    }
    const result = {};
    for (const [name, n] of Object.entries(values))
        result[name] = opts.format(n);
    return result;
}
/** A random needs a real interval and a usable step — anything else never rolls, so treating it as
 * a variable at all is a lie. Mirrors the app's `isRollableVariable`, which asks the same question
 * of the same fields. */
export function isUsableRange(v) {
    return v.min != null && v.max != null && v.step != null
        && !isNaN(v.min) && !isNaN(v.max) && !isNaN(v.step)
        && v.max > v.min && v.step > 0;
}
/** How many steps fit in the range. The grid is `count + 1` points, both ends included. */
function gridCount(v) {
    return Math.floor((v.max - v.min) / v.step);
}
/**
 * The exponent `skew` becomes. 1 is the uniform draw, and that is what no skew resolves to.
 *
 * `skew` is read as the share landing in the near half, so the exponent is whatever makes that
 * share come out: drawing `u ** (1 / e)` puts `0.5 ** e` of its mass below the midpoint, and
 * setting that equal to the stated share gives `e = log(share) / log(0.5)`.
 */
function skewExponent(v) {
    if (v.skew == null || isNaN(v.skew) || v.skew <= 50)
        return 1;
    return Math.log(Math.min(v.skew, MAX_SKEW) / 100) / Math.log(0.5);
}
/**
 * THE draw, from a uniform `u` in [0, 1). Both draw sites call this and neither does the
 * arithmetic itself — they were two copies with a comment on each saying they must not disagree,
 * which is a rule a reviewer has to enforce rather than one the code enforces.
 *
 * `Math.floor(u * (count + 1))`, not `Math.round(u * count)`: rounding gives the two ENDPOINTS
 * half the probability of every interior step, so a 1-to-10 variable drew 1 and 10 half as often
 * as 5.
 *
 * The skew is applied to the INDEX. That is the whole design: the index is `0..count` and never
 * sees a value, so a range crossing or starting at zero skews exactly as well as one that does
 * not, where a logarithmic draw would be undefined. It also leaves the reachable set alone.
 */
export function drawFromUnit(v, u) {
    const count = gridCount(v);
    const e = skewExponent(v);
    const biased = e === 1 ? u : Math.pow(u, 1 / e);
    const i = Math.min(count, Math.floor(biased * (count + 1)));
    return v.min + (v.skewToward === 'max' ? count - i : i) * v.step;
}
/**
 * The share of draws landing on the SINGLE likeliest value — the skewed end's own grid point.
 *
 * This, not the strength, is what says whether a variable has stopped varying, because the same
 * strength means very different things at different grid sizes: 85 leaves a 2493-point range
 * drawing its minimum 16% of the time and a 3-point range drawing it 77% of the time. A fixed
 * cap on the strength cannot express that; this can.
 */
export function skewModeShare(v) {
    const n = gridCount(v) + 1;
    if (n <= 1)
        return 1;
    return Math.pow(1 / n, skewExponent(v));
}
/** A skew so strong the variable mostly rolls one value. A WARNING, not an error: it is a
 *  coherent thing to have authored, just almost certainly not what was meant. */
export function isDegenerateSkew(v) {
    if (v.skew == null || isNaN(v.skew) || v.skew <= 50)
        return false;
    if (!isUsableRange(v))
        return false;
    return skewModeShare(v) > DEGENERATE_SKEW_SHARE;
}
/** The value a degenerate skew keeps landing on, so the warning can name it. */
export function skewModeValue(v) {
    return v.skewToward === 'max' ? v.min + gridCount(v) * v.step : v.min;
}
/**
 * Resolves all task variables to string values reproducibly, from the seed stored with the attempt.
 *
 * Each random gets its own sub-seed derived from `attemptSeed` and `variable.id`, so reordering
 * variables doesn't change any individual variable's rolled value.
 */
export function computeVariableValues(attemptSeed, variables) {
    return resolveVariables(variables, {
        format: fmtValue,
        sampleRandom: (v) => {
            // The arithmetic lives in `drawFromUnit`, which the preview draw calls too: this seeded
            // path and that unseeded one differ ONLY in where the uniform number comes from.
            const rand = mulberry32(deriveSeed(attemptSeed, v.id));
            return drawFromUnit(v, rand());
        },
    });
}
/**
 * Generates a random 32-bit seed suitable for use as an attempt seed.
 * Call this once at attempt-start and persist the result.
 */
export function generateAttemptSeed() {
    return (Math.random() * 0x100000000) >>> 0;
}
//# sourceMappingURL=variables.js.map